/**
 * Secondary valuation orders API (eastview-os `app/routers/appraisal_review.py`,
 * contract: frontend_documentation/appraisal-review.md § Secondary orders).
 *
 * One record per ORDER for a second opinion of value on one subject property
 * (`loan_processing.secondary_valuation_orders`). Kinds (`SecondaryKind`,
 * verbatim): `internal` | `vendor_amc` | `richer_values` | `avm`.
 *
 * Lifecycle (`SecondaryOrderStatus`, verbatim):
 *   requested → submitted → received → tied_out, cancelled from any non-terminal
 *   state. Every transition is guarded server-side on its allowed from-states;
 *   a stale action answers 409 ("refetch it").
 *
 * `requestedBy` is set by the backend from the logged-in user — never sent.
 *
 * The Orders tab uses `vendor_amc` for "ordered through an outside party":
 * the backend records it as `requested` with `submission.status =
 * not_configured` (Reggora placement is not automated), so the desk records the
 * vendor's order number with `markSubmitted` and the figures with
 * `markReceived`. The `internal` kind is NOT used from here — the internal
 * review keeps its own `/loan-processing/{dealId}/appraisal-orders` flow.
 */
import { losHttp } from "../lib/losHttp";

const BASE = "/appraisal-review";

// `{ $oid }` / ObjectId-ish → plain string.
const oidStr = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v.$oid || v._id || null;
  return String(v);
};

/** Stable string id of an order. */
export const secondaryOrderId = (order) => oidStr(order?._id) || oidStr(order?.oid);

// Drop undefined / null / "" keys so optional fields are simply absent.
const prune = (body) =>
  Object.fromEntries(
    Object.entries(body || {}).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

export const SECONDARY_KIND = {
  INTERNAL: "internal",
  VENDOR_AMC: "vendor_amc",
  RICHER_VALUES: "richer_values",
  AVM: "avm",
};

export const SECONDARY_STATUS = {
  REQUESTED: "requested",
  SUBMITTED: "submitted",
  RECEIVED: "received",
  TIED_OUT: "tied_out",
  CANCELLED: "cancelled",
};

// Allowed from-states, copied from SecondaryValuationOrdersStore so the UI only
// offers the actions the backend will accept.
const CAN = {
  submitted: [SECONDARY_STATUS.REQUESTED],
  received: [SECONDARY_STATUS.REQUESTED, SECONDARY_STATUS.SUBMITTED],
  tieOut: [SECONDARY_STATUS.RECEIVED],
  cancel: [
    SECONDARY_STATUS.REQUESTED,
    SECONDARY_STATUS.SUBMITTED,
    SECONDARY_STATUS.RECEIVED,
  ],
};
export const canMarkSubmitted = (o) => CAN.submitted.includes(o?.status);
export const canMarkReceived = (o) => CAN.received.includes(o?.status);
export const canTieOut = (o) => CAN.tieOut.includes(o?.status);
export const canCancel = (o) => CAN.cancel.includes(o?.status);

/** The message to show for a failed call (the backend names the problem). */
export const secondaryOrderErrorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 409)
    return "This order was updated by someone else. The list has been refreshed — check its status and try again.";
  if (typeof detail === "string" && detail) return detail;
  return fallback || "Something went wrong. Please try again.";
};

export const secondaryValuationOrdersApi = {
  // GET /appraisal-review/{dealId}/secondary-orders?subjectPropertyId=
  // → { dealId, orders: SecondaryValuationOrder[] } (oldest first)
  list: (dealId, subjectPropertyId) =>
    losHttp
      .get(`${BASE}/${dealId}/secondary-orders`, {
        params: subjectPropertyId ? { subjectPropertyId } : undefined,
      })
      .then((r) => (Array.isArray(r.data?.orders) ? r.data.orders : [])),

  // GET /appraisal-review/secondary-orders/queue?status=&kind=
  // → { orders, counts, providers } — every outside order across all loans
  // (never `internal`), oldest first, capped at 500 server-side.
  queue: ({ status, kind } = {}) =>
    losHttp
      .get(`${BASE}/secondary-orders/queue`, {
        params: prune({ status, kind }),
      })
      .then((r) => (Array.isArray(r.data?.orders) ? r.data.orders : [])),

  // GET /appraisal-review/secondary-orders/{orderId} → SecondaryValuationOrder
  get: (orderId) =>
    losHttp.get(`${BASE}/secondary-orders/${orderId}`).then((r) => r.data),

  // POST /appraisal-review/{dealId}/secondary-orders → 201 SecondaryValuationOrder
  // body { subjectPropertyId, kind, provider?, appraisalDocOids?, instructions?,
  //        requestedByDate? (YYYY-MM-DD) }
  create: (dealId, body) =>
    losHttp
      .post(`${BASE}/${dealId}/secondary-orders`, prune(body))
      .then((r) => r.data),

  // POST …/{orderId}/submitted { providerOrderId, note? } — manual submission record
  markSubmitted: (orderId, { providerOrderId, note } = {}) =>
    losHttp
      .post(
        `${BASE}/secondary-orders/${orderId}/submitted`,
        prune({ providerOrderId, note }),
      )
      .then((r) => r.data),

  // POST …/{orderId}/received { asIsValue?, arv?, effectiveDate?, confidenceScore?,
  //   valueRangeLow?, valueRangeHigh?, documentOid?, source?, note? }
  markReceived: (orderId, body) =>
    losHttp
      .post(`${BASE}/secondary-orders/${orderId}/received`, prune(body))
      .then((r) => r.data),

  // POST …/{orderId}/tie-out { outcome?: accepted|variance_flagged|rejected, notes? }
  tieOut: (orderId, body) =>
    losHttp
      .post(`${BASE}/secondary-orders/${orderId}/tie-out`, prune(body))
      .then((r) => r.data),

  // POST …/{orderId}/cancel { reason? }
  cancel: (orderId, { reason } = {}) =>
    losHttp
      .post(`${BASE}/secondary-orders/${orderId}/cancel`, prune({ reason }))
      .then((r) => r.data),
};

export default secondaryValuationOrdersApi;
